import React, { useEffect, useRef, useState } from 'react';
import { MessageSquare, CircleAlert, Loader2, PaperclipIcon, ImageIcon, X, Monitor, Smartphone, Tablet, CheckCircle } from 'lucide-react';
import { Switch } from "../ui/switch";
import { Label } from "../ui/label";

function ChatPanel({ 
  messages, 
  userInput, 
  onUserInput, 
  onSendMessage, 
  isGenerating = false,
  onSubmitModification,
  onChatMessage,  // New prop for handling chat messages
  analyticsConsentGiven, // Accept the consent state prop
  onDeviceTypeChange, // <<< Accept prop from App.js
  currentDeviceType // <<< Accept prop from App.js
}) {
  const messagesEndRef = useRef(null); // Ref to scroll to bottom
  const [waitingForModification, setWaitingForModification] = useState(false);
  const [modificationText, setModificationText] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [designStyle, setDesignStyle] = useState('creative'); // Add state for design style, default creative
  const fileInputRef = useRef(null);
  const lastUserMessage = useRef('');
  
  // Auto-resizing textarea refs
  const userInputRef = useRef(null);
  const modificationInputRef = useRef(null);
  const [showPasteNotification, setShowPasteNotification] = useState(false);

  // --- Track typing --- 
  // Use a ref to track if typing has been logged for the current input session
  const typingLoggedRef = useRef(false);
  // Debounce function
  const debounce = (func, delay) => {
    let timeoutId;
    return function(...args) {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        func.apply(this, args);
      }, delay);
    };
  };
  // Debounced typing logger
  const logTypingEventDebounced = useRef(
    debounce(() => {
      // Check consent before logging
      if (analyticsConsentGiven && typeof window.gtag === 'function') { 
        console.log("Logging typing event"); // Debug log
        window.gtag('event', 'chat_typing', {
          'event_category': 'engagement',
          'event_label': 'User started typing in chat'
        });
      }
    }, 1000) // Log only after 1 second of inactivity after typing starts
  ).current;

  // Handler for textarea change
  const handleUserInput = (value) => {
    onUserInput(value); // Call the original handler
    // Log typing event only once per input session (until message is sent or cleared)
    if (value.length > 0 && !typingLoggedRef.current) {
      logTypingEventDebounced();
      typingLoggedRef.current = true; // Mark as logged for this session
    }
  };
  // --- End Track typing ---

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    
    // Check if the last message is asking for element modification
    const lastMessage = messages[messages.length - 1];
    if (lastMessage && lastMessage.role === 'system' && 
        lastMessage.content.includes('changes would you like to make to this element')) {
      setWaitingForModification(true);
    } else {
      setWaitingForModification(false);
      setModificationText('');
    }

    // Store the last user message for potential device type changes
    const lastUserMsg = messages.filter(msg => msg.role === 'user').pop();
    if (lastUserMsg) {
      lastUserMessage.current = lastUserMsg.content;
    }
  }, [messages]);

  // Handle auto-resize of textareas when content changes
  useEffect(() => {
    if (userInputRef.current) {
      // Reset height to get the correct scrollHeight
      userInputRef.current.style.height = "0px";
      // Set to scrollHeight to expand the textarea
      const scrollHeight = userInputRef.current.scrollHeight;
      userInputRef.current.style.height = Math.min(scrollHeight, 150) + "px";
    }
  }, [userInput]);

  useEffect(() => {
    if (modificationInputRef.current) {
      // Reset height to get the correct scrollHeight
      modificationInputRef.current.style.height = "0px";
      // Set to scrollHeight to expand the textarea
      const scrollHeight = modificationInputRef.current.scrollHeight;
      modificationInputRef.current.style.height = Math.min(scrollHeight, 150) + "px";
    }
  }, [modificationText]);

  // Handle changing device type
  const handleDeviceTypeChange = (newDeviceType) => {
    // Call the handler passed from App.js directly
    if (onDeviceTypeChange && currentDeviceType !== newDeviceType) {
        onDeviceTypeChange(newDeviceType);
    }
  };

  // Handle submission of modification request
  const handleModificationSubmit = () => {
    if ((modificationText.trim() || attachments.length > 0) && onSubmitModification) {
      // Include designStyle when submitting modification
      onSubmitModification(modificationText, attachments, currentDeviceType, designStyle); 
      setModificationText('');
      setAttachments([]);
      setWaitingForModification(false);
    }
  };

  // Handle file selection
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const newAttachments = files.map(file => ({
      file,
      preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
      name: file.name,
      type: file.type
    }));
    
    setAttachments([...attachments, ...newAttachments]);
    e.target.value = null; // Reset input
  };

  // Remove an attachment
  const removeAttachment = (index) => {
    const newAttachments = [...attachments];
    if (newAttachments[index].preview) {
      URL.revokeObjectURL(newAttachments[index].preview);
    }
    newAttachments.splice(index, 1);
    setAttachments(newAttachments);
  };

  // Handle send message with attachments and device type
  const handleSendWithAttachments = () => {
    if ((userInput.trim() || attachments.length > 0) && !isGenerating) {
      // Track event with Google Analytics only if consent is given
      if (analyticsConsentGiven && typeof window.gtag === 'function') { 
        window.gtag('event', 'send_message_click', {
          'event_category': 'engagement',
          'event_label': 'Chat Send Button Click'
          // Optionally, track if attachments were included
          // 'has_attachments': attachments.length > 0
        });
      }
      typingLoggedRef.current = false; // Reset typing log status after sending

      if (onSendMessage) {
        // If there are attachments, always treat as a design query
        if (attachments.length > 0) {
          // Include designStyle when sending message with attachments
          onSendMessage(userInput, attachments, currentDeviceType, designStyle); 
          lastUserMessage.current = userInput;
          setAttachments([]);
        } else {
          // Text-only - pass to chat processing first
          if (typeof onChatMessage === 'function') {
             // Include designStyle for chat messages too, in case they trigger design
            onChatMessage(userInput, currentDeviceType, designStyle);
          } else {
            // Fallback to direct design generation if chat handling not available
            // Include designStyle here too
            onSendMessage(userInput, [], currentDeviceType, designStyle); 
          }
          lastUserMessage.current = userInput;
        }
      }
    }
  };

  // Handle paste event for images
  const handlePaste = (e) => {
    if (isGenerating) return;
    
    const items = e.clipboardData?.items;
    if (!items) return;

    let imageAdded = false;
    
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf('image') !== -1) {
        const file = items[i].getAsFile();
        if (file) {
          const attachment = {
            file,
            preview: URL.createObjectURL(file),
            name: `Pasted Image ${new Date().toISOString().slice(0, 19).replace('T', ' ')}`,
            type: file.type
          };
          
          setAttachments(prev => [...prev, attachment]);
          imageAdded = true;
        }
      }
    }
    
    // Show paste notification
    if (imageAdded) {
      setShowPasteNotification(true);
      setTimeout(() => {
        setShowPasteNotification(false);
      }, 3000); // Hide after 3 seconds
    }
  };

  // Device type button component - fixed to properly handle active state
  const DeviceTypeButton = ({ type, icon, size, strokeWidth = 2 }) => {
    // Use prop currentDeviceType instead of local state
    const isActive = currentDeviceType === type; 
    
    return (
      <button
        onClick={() => handleDeviceTypeChange(type)}
        className={`p-1.5 rounded ${isActive ? 'bg-white dark:bg-dark-bg-primary shadow' : 'hover:bg-gray-200 dark:hover:bg-dark-bg-secondary'}`}
        title={`${type.charAt(0).toUpperCase() + type.slice(1)} Design`}
      >
        {React.cloneElement(icon, { 
          size, 
          strokeWidth,
          className: isActive ? 'text-blue-600 dark:text-dark-accent' : 'text-gray-500 dark:text-dark-text-secondary'
        })}
      </button>
    );
  };

  return (
    <div className="w-80 min-w-[320px] border-l border-gray-200 dark:border-dark-border bg-white dark:bg-dark-bg-secondary flex flex-col shrink-0 z-10 h-full">
      {/* Messages Area */} 
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((message, index) => (
          <div 
            key={index} 
            className={`message ${
              message.role === 'user' ? 'text-right' : ''
            }`}
          >
            <div 
              className={`inline-block rounded-lg px-4 py-2 max-w-[85%] ${
                message.role === 'user' 
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 dark:bg-dark-bg-primary dark:text-dark-text-primary'
              }`}
            >
              {/* Special formatting for code blocks */}
              {message.content.includes('```') ? (
                <div className="whitespace-pre-wrap">
                  {message.content.split('```').map((part, i) => (
                    i % 2 === 0 ? (
                      <span key={i}>{part}</span>
                    ) : (
                      <pre key={i} className="mt-2 p-2 bg-gray-800 text-gray-200 rounded overflow-x-auto text-sm font-mono">{part}</pre>
                    )
                  ))}
                </div>
              ) : (
                <div className="whitespace-pre-wrap">
                  {message.role === 'system' && message.content.includes('⚠️') ? (
                    <div className="flex items-start">
                      <CircleAlert className="shrink-0 h-5 w-5 mr-2 text-yellow-500" />
                      <span>{message.content}</span>
                    </div>
                  ) : message.content}
                </div>
              )}
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>
      
      {/* Device Type and Style Selector */}
      <div className="p-2 border-t border-gray-200 dark:border-dark-border flex items-center justify-center space-x-4 bg-gray-50 dark:bg-dark-bg-primary">
        {/* Device Type Buttons */}
        <div className="flex space-x-1">
            <DeviceTypeButton type="web" icon={<Monitor />} size={16} />
            <DeviceTypeButton type="mobile" icon={<Smartphone />} size={16} />
            <DeviceTypeButton type="tablet" icon={<Tablet />} size={16} />
        </div>
        
        {/* Separator */}
        <div className="h-6 w-px bg-gray-300 dark:bg-dark-border"></div>

        {/* Design Style Toggle */}
        <div className="flex items-center space-x-2">
          <Switch
            id="design-style-switch"
            checked={designStyle === 'creative'}
            onCheckedChange={(checked) => setDesignStyle(checked ? 'creative' : 'wireframe')}
            aria-label="Toggle between Creative and Wireframe design style"
          />
          <Label htmlFor="design-style-switch" className="text-xs text-gray-600 dark:text-dark-text-secondary">
            {designStyle === 'creative' ? 'Creative' : 'Wireframe'}
          </Label>
        </div>
      </div>
      
      {/* Input Area */}
      {waitingForModification ? (
        <div className="border-t border-gray-200 dark:border-dark-border p-3 pb-4">
          <div className="mb-2 text-sm text-gray-500 dark:text-dark-text-secondary">Describe your modifications:</div>
          <div className="relative">
            <textarea
              ref={modificationInputRef}
              value={modificationText}
              onChange={(e) => setModificationText(e.target.value)}
              placeholder="Describe the changes you want..."
              className="w-full p-3 pr-10 border border-gray-300 dark:border-dark-border dark:bg-dark-bg-primary dark:text-dark-text-primary rounded-lg focus:outline-none focus:border-blue-500 resize-none min-h-[80px]"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleModificationSubmit();
                }
              }}
              onPaste={handlePaste}
              disabled={isGenerating}
            />
            <div className="absolute bottom-3 right-3 flex space-x-1">
              <button
                onClick={() => fileInputRef.current?.click()}
                className="text-gray-500 dark:text-dark-text-secondary p-1 hover:text-blue-600 dark:hover:text-dark-accent"
                title="Attach image"
                disabled={isGenerating}
              >
                <ImageIcon size={18} />
              </button>
            </div>
          </div>
          
          {/* Attachments */}
          {attachments.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2">
              {attachments.map((attachment, index) => (
                <div 
                  key={index} 
                  className="relative group border border-gray-300 dark:border-dark-border rounded-md overflow-hidden"
                >
                  {attachment.preview ? (
                    <div className="w-16 h-16 relative">
                      <img src={attachment.preview} alt="" className="w-full h-full object-cover" />
                      <button 
                        onClick={() => removeAttachment(index)}
                        className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ) : (
                    <div className="bg-gray-100 dark:bg-dark-bg-primary p-2 text-xs text-gray-800 dark:text-dark-text-primary">
                      {attachment.name.slice(0, 15)}{attachment.name.length > 15 ? '...' : ''}
                      <button 
                        onClick={() => removeAttachment(index)}
                        className="absolute top-0 right-0 bg-red-500 text-white rounded-full p-0.5"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          
          <div className="mt-2 flex justify-end">
            <button 
              onClick={handleModificationSubmit}
              disabled={(!modificationText.trim() && attachments.length === 0) || isGenerating}
              className={`px-4 py-2 rounded-md font-medium text-sm ${
                (!modificationText.trim() && attachments.length === 0) || isGenerating
                  ? 'bg-gray-300 dark:bg-dark-bg-primary text-gray-500 dark:text-dark-text-secondary'
                  : 'bg-blue-600 dark:bg-dark-accent text-white hover:bg-blue-700 dark:hover:bg-blue-700'
              }`}
            >
              {isGenerating ? (
                <div className="flex items-center">
                  <Loader2 size={16} className="animate-spin mr-1" />
                  <span>Processing...</span>
                </div>
              ) : 'Apply Changes'}
            </button>
          </div>
          <input 
            type="file" 
            ref={fileInputRef} 
            className="hidden" 
            accept="image/*" 
            onChange={handleFileChange} 
            multiple
          />
        </div>
      ) : (
        <div className="border-t border-gray-200 dark:border-dark-border p-3 pb-4">
          <div className="relative">
            <textarea
              ref={userInputRef}
              value={userInput}
              onChange={(e) => handleUserInput(e.target.value)}
              placeholder="Describe your idea or upload an image..."
              className="w-full p-3 pr-10 border border-gray-300 dark:border-dark-border dark:bg-dark-bg-primary dark:text-dark-text-primary rounded-lg focus:outline-none focus:border-blue-500 resize-none min-h-[80px]"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendWithAttachments();
                }
              }}
              onPaste={handlePaste}
              disabled={isGenerating}
            />
            <div className="absolute bottom-3 right-3 flex space-x-1">
              <button
                onClick={() => fileInputRef.current?.click()}
                className="text-gray-500 dark:text-dark-text-secondary p-1 hover:text-blue-600 dark:hover:text-dark-accent"
                title="Attach image"
                disabled={isGenerating}
              >
                <PaperclipIcon size={18} />
              </button>
              <button
                onClick={handleSendWithAttachments}
                disabled={(!userInput.trim() && attachments.length === 0) || isGenerating}
                className={`flex items-center justify-center text-white rounded-full w-6 h-6 ${
                  (!userInput.trim() && attachments.length === 0) || isGenerating
                    ? 'bg-gray-300 dark:bg-dark-bg-primary text-gray-500 dark:text-dark-text-secondary'
                    : 'bg-blue-600 dark:bg-dark-accent hover:bg-blue-700 dark:hover:bg-blue-700'
                }`}
                title="Send message"
              >
                {isGenerating ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <MessageSquare size={14} />
                )}
              </button>
            </div>
          </div>
          
          {/* Attachments */}
          {attachments.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2">
              {attachments.map((attachment, index) => (
                <div 
                  key={index} 
                  className="relative group border border-gray-300 dark:border-dark-border rounded-md overflow-hidden"
                >
                  {attachment.preview ? (
                    <div className="w-16 h-16 relative">
                      <img src={attachment.preview} alt="" className="w-full h-full object-cover" />
                      <button 
                        onClick={() => removeAttachment(index)}
                        className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ) : (
                    <div className="bg-gray-100 dark:bg-dark-bg-primary p-2 text-xs text-gray-800 dark:text-dark-text-primary">
                      {attachment.name.slice(0, 15)}{attachment.name.length > 15 ? '...' : ''}
                      <button 
                        onClick={() => removeAttachment(index)}
                        className="absolute top-0 right-0 bg-red-500 text-white rounded-full p-0.5"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          
          <input 
            type="file" 
            ref={fileInputRef} 
            className="hidden" 
            accept="image/*" 
            onChange={handleFileChange} 
            multiple
          />
          
          {/* Image paste notification */}
          {showPasteNotification && (
            <div className="fixed bottom-16 left-1/2 transform -translate-x-1/2 bg-black/90 text-white px-4 py-2 rounded-full shadow-lg flex items-center space-x-2 text-sm">
              <CheckCircle size={16} className="text-green-400" />
              <span>Image pasted successfully</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default ChatPanel; 